import { useEffect, useState } from "react";
import { format, startOfMonth, endOfMonth } from "date-fns";
import { DownloadSimple, PencilLine, Trash } from "@phosphor-icons/react";
import api, { apiError, API_BASE } from "../lib/api";
import { zar, fmtDateTime } from "../lib/format";
import { useAuth } from "../context/AuthContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from "../components/ui/alert-dialog";
import { toast } from "sonner";

const MANUAL_FIELDS = ["advance", "uniform", "uif", "paye"];
const MANUAL_LABELS = { advance: "Advance", uniform: "Uniform", uif: "UIF", paye: "PAYE" };

export default function Payroll() {
  const { user } = useAuth();
  const isOwner = user.role === "owner";
  const [runs, setRuns] = useState([]);
  const [selected, setSelected] = useState(null);
  const [start, setStart] = useState(format(startOfMonth(new Date()), "yyyy-MM-dd"));
  const [end, setEnd] = useState(format(endOfMonth(new Date()), "yyyy-MM-dd"));
  const [tipStart, setTipStart] = useState(format(startOfMonth(new Date()), "yyyy-MM-dd"));
  const [tipEnd, setTipEnd] = useState(format(endOfMonth(new Date()), "yyyy-MM-dd"));
  const [preview, setPreview] = useState(null);
  const [manual, setManual] = useState({});
  const [busy, setBusy] = useState(false);
  const [amending, setAmending] = useState(false);
  const [amendVals, setAmendVals] = useState({});

  const loadRuns = () => api.get("/payroll/runs").then((r) => setRuns(r.data)).catch(() => {});
  useEffect(() => { loadRuns(); }, []);

  const openRun = (id) => {
    setAmending(false);
    api.get(`/payroll/runs/${id}`).then((r) => setSelected(r.data)).catch(() => toast.error("Failed to load run"));
  };

  const startAmend = () => {
    const m = {};
    selected.lines.forEach((l) => {
      m[l.employee_id] = {
        basic_pay: l.basic_pay, tips: l.tips, advance: l.advance, uniform: l.uniform,
        unpaid_leave: l.unpaid_leave, uif: l.uif, paye: l.paye,
      };
    });
    setAmendVals(m);
    setAmending(true);
  };

  const saveAmend = async () => {
    setBusy(true);
    try {
      const payload = {
        lines: Object.entries(amendVals).map(([employee_id, v]) => ({
          employee_id,
          basic_pay: Number(v.basic_pay) || 0, tips: Number(v.tips) || 0,
          advance: Number(v.advance) || 0, uniform: Number(v.uniform) || 0,
          unpaid_leave: Number(v.unpaid_leave) || 0, uif: Number(v.uif) || 0, paye: Number(v.paye) || 0,
        })),
      };
      const { data } = await api.put(`/payroll/runs/${selected.id}/amend`, payload);
      toast.success("Run amended — audit log updated, affected staff notified");
      setSelected(data);
      setAmending(false);
      loadRuns();
    } catch (e) {
      toast.error(apiError(e, "Amend failed"));
    } finally {
      setBusy(false);
    }
  };

  const deleteRun = async () => {
    setBusy(true);
    try {
      await api.delete(`/payroll/runs/${selected.id}`);
      toast.success("Payroll run deleted — recorded in the audit log");
      setSelected(null);
      await loadRuns();
    } catch (e) {
      toast.error(apiError(e, "Delete failed"));
    } finally {
      setBusy(false);
    }
  };

  const runPreview = async () => {
    setBusy(true);
    try {
      const { data } = await api.get(`/payroll/preview?start=${start}&end=${end}&tip_start=${tipStart}&tip_end=${tipEnd}`);
      setPreview(data);
      const m = {};
      data.lines.forEach((l) => { m[l.employee_id] = { advance: 0, uniform: 0, uif: 0, paye: 0 }; });
      setManual(m);
      setSelected(null);
    } catch (e) {
      toast.error(apiError(e, "Preview failed"));
    } finally {
      setBusy(false);
    }
  };

  const finalize = async () => {
    setBusy(true);
    try {
      const payload = {
        period_start: start, period_end: end, tip_start: tipStart, tip_end: tipEnd,
        manual: Object.entries(manual).map(([employee_id, v]) => ({
          employee_id,
          advance: Number(v.advance) || 0, uniform: Number(v.uniform) || 0,
          uif: Number(v.uif) || 0, paye: Number(v.paye) || 0,
        })),
      };
      const { data } = await api.post("/payroll/runs", payload);
      toast.success("Payroll run finalized — payslips generated and staff notified");
      setPreview(null);
      setSelected(data);
      loadRuns();
    } catch (e) {
      toast.error(apiError(e, "Finalize failed"));
    } finally {
      setBusy(false);
    }
  };

  const lineNet = (l) => {
    const m = manual[l.employee_id] || { advance: 0, uniform: 0, uif: 0, paye: 0 };
    return l.gross - (l.unpaid_leave + (Number(m.advance) || 0) + (Number(m.uniform) || 0) + (Number(m.uif) || 0) + (Number(m.paye) || 0));
  };

  const manualCell = (l, f) => (
    <td key={f} className="py-2 pr-3 num">
      <Input type="number" min="0" step="any" className="w-24 h-8 text-right ml-auto" data-testid={`manual-${f}-${l.employee_id}`}
        value={manual[l.employee_id]?.[f] ?? 0}
        onChange={(e) => setManual((m) => ({ ...m, [l.employee_id]: { ...m[l.employee_id], [f]: e.target.value } }))} />
    </td>
  );

  const linesTable = (lines, editable) => (
    <div className="overflow-x-auto">
      <table className="w-full text-sm min-w-[1150px]">
        <thead>
          <tr className="border-b text-left text-muted-foreground">
            <th className="py-2 pr-3 font-medium">Employee</th>
            <th className="py-2 pr-3 font-medium text-right">Hours</th>
            <th className="py-2 pr-3 font-medium text-right">Basic</th>
            <th className="py-2 pr-3 font-medium text-right">Tips</th>
            <th className="py-2 pr-3 font-medium text-right">Gross</th>
            {MANUAL_FIELDS.map((f) => <th key={f} className="py-2 pr-3 font-medium text-right">{MANUAL_LABELS[f]}</th>)}
            <th className="py-2 pr-3 font-medium text-right">Unpaid leave</th>
            <th className="py-2 pr-3 font-medium text-right">Net</th>
            {!editable && <th className="py-2 font-medium text-right">Payslip</th>}
          </tr>
        </thead>
        <tbody>
          {lines.map((l) => (
            <tr key={l.employee_id} className="border-b last:border-0" data-testid={`payroll-line-${l.employee_id}`}>
              <td className="py-2 pr-3">
                <span className="font-medium">{l.name}</span>
                <span className="block text-xs text-muted-foreground">{l.salary_type === "hourly" ? `${zar(l.rate)}/h` : `${zar(l.rate)}/mo`}</span>
              </td>
              <td className="py-2 pr-3 num">{l.normal_hours}h{l.holiday_hours ? ` +${l.holiday_hours}h PH` : ""}</td>
              <td className="py-2 pr-3 num">{zar(l.basic_pay)}</td>
              <td className="py-2 pr-3 num">{zar(l.tips)}</td>
              <td className="py-2 pr-3 num font-medium">{zar(l.gross)}</td>
              {MANUAL_FIELDS.map((f) => (editable ? manualCell(l, f) : <td key={f} className="py-2 pr-3 num">{zar(l[f])}</td>))}
              <td className="py-2 pr-3 num">{zar(l.unpaid_leave)}<span className="block text-[10px] text-muted-foreground">{l.unpaid_leave_days}d</span></td>
              <td className="py-2 pr-3 num font-semibold">{zar(editable ? lineNet(l) : l.net)}</td>
              {!editable && (
                <td className="py-2 text-right">
                  <a href={`${API_BASE}/payroll/runs/${selected.id}/payslip/${l.employee_id}`} data-testid={`payslip-${l.employee_id}`}>
                    <Button variant="outline" size="sm"><DownloadSimple size={14} className="mr-1" /> PDF</Button>
                  </a>
                </td>
              )}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );

  return (
    <div className="space-y-6" data-testid="payroll-page">
      <div>
        <h1 className="font-heading text-3xl font-bold">Payroll</h1>
        <p className="text-muted-foreground mt-1">Net = Gross − (advance + uniform + unpaid leave + UIF + PAYE) · UIF and PAYE are entered manually by your accountant</p>
      </div>

      <Card className="border-border shadow-none" data-testid="new-run-card">
        <CardHeader><CardTitle className="font-heading text-lg">New payroll run</CardTitle></CardHeader>
        <CardContent className="space-y-4">
          <div className="flex flex-wrap items-end gap-3">
            <div className="space-y-1"><Label>Period start</Label><Input type="date" data-testid="payroll-start" value={start} onChange={(e) => setStart(e.target.value)} /></div>
            <div className="space-y-1"><Label>Period end</Label><Input type="date" data-testid="payroll-end" value={end} onChange={(e) => setEnd(e.target.value)} /></div>
            <div className="space-y-1">
              <Label>Tip period start</Label>
              <Input type="date" data-testid="tip-period-start" value={tipStart} onChange={(e) => setTipStart(e.target.value)} />
            </div>
            <div className="space-y-1">
              <Label>Tip period end</Label>
              <Input type="date" data-testid="tip-period-end" value={tipEnd} onChange={(e) => setTipEnd(e.target.value)} />
            </div>
            <Button onClick={runPreview} disabled={busy} variant="outline" data-testid="payroll-preview-btn">
              {busy ? "Working…" : "1 · Preview"}
            </Button>
            {preview && isOwner && (
              <Button onClick={finalize} disabled={busy} data-testid="payroll-finalize-btn" className="bg-primary hover:bg-[#8B5CF6] text-white">
                2 · Finalize run
              </Button>
            )}
            {preview && !isOwner && <p className="text-xs text-muted-foreground self-end">Only the owner can finalize a payroll run.</p>}
          </div>
          <p className="text-xs text-muted-foreground">
            Wages/hours use the payroll period; tips are summed over the tip period (defaults to the same range — set it separately if your venue settles tips on a different cycle, e.g. tips calendar-monthly vs payroll 19th–18th).
          </p>
          {preview && (
            <div data-testid="payroll-preview">
              <p className="text-sm text-muted-foreground mb-3">
                Step 2: enter advance / uniform / UIF / PAYE per employee, then finalize. Public-holiday hours are paid at 2×.
              </p>
              {linesTable(preview.lines, true)}
            </div>
          )}
        </CardContent>
      </Card>

      {selected && (
        <Card className="border-border shadow-none" data-testid="run-detail">
          <CardHeader className="flex flex-row flex-wrap items-center justify-between gap-3">
            <CardTitle className="font-heading text-lg">
              Run {selected.period_start} → {selected.period_end}
              <span className="block text-xs font-normal text-muted-foreground mt-1">
                Finalized by {selected.created_by_name} · {fmtDateTime(selected.created_at)} · Gross {zar(selected.totals.gross)} · Net {zar(selected.totals.net)}
                {(selected.tip_start || selected.tip_end) && (selected.tip_start !== selected.period_start || selected.tip_end !== selected.period_end) && (
                  <> · <span data-testid="run-tip-period">Tips settled {selected.tip_start} → {selected.tip_end}</span></>
                )}
                {selected.amended_at && <> · <span className="text-[#8a6414]">Amended by {selected.amended_by_name} · {fmtDateTime(selected.amended_at)}</span></>}
              </span>
            </CardTitle>
            <div className="flex items-center gap-2">
              <a href={`${API_BASE}/payroll/runs/${selected.id}/csv`} data-testid="run-csv-export">
                <Button variant="outline" size="sm"><DownloadSimple size={14} className="mr-1" /> CSV export</Button>
              </a>
              {isOwner && !amending && (
                <Button variant="outline" size="sm" onClick={startAmend} data-testid="amend-run-btn">
                  <PencilLine size={14} className="mr-1" /> Amend run
                </Button>
              )}
              {isOwner && (
                <AlertDialog>
                  <AlertDialogTrigger asChild>
                    <Button variant="outline" size="sm" data-testid="delete-run-btn"
                      className="text-destructive border-destructive/40 hover:bg-destructive/10 hover:text-destructive">
                      <Trash size={14} className="mr-1" /> Delete
                    </Button>
                  </AlertDialogTrigger>
                  <AlertDialogContent data-testid="delete-run-dialog">
                    <AlertDialogHeader>
                      <AlertDialogTitle>Delete this payroll run?</AlertDialogTitle>
                      <AlertDialogDescription>
                        This permanently deletes the run for {selected.period_start} → {selected.period_end}
                        (gross {zar(selected.totals.gross)}, {selected.lines.length} employees) and its payslips.
                        The deletion is recorded in the audit log. This cannot be undone.
                      </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                      <AlertDialogCancel data-testid="delete-run-cancel">Cancel</AlertDialogCancel>
                      <AlertDialogAction onClick={deleteRun} disabled={busy} data-testid="delete-run-confirm"
                        className="bg-destructive text-white hover:bg-destructive/90">
                        Delete run
                      </AlertDialogAction>
                    </AlertDialogFooter>
                  </AlertDialogContent>
                </AlertDialog>
              )}
            </div>
          </CardHeader>
          <CardContent>
            {amending ? (
              <div className="space-y-4" data-testid="amend-table">
                <p className="text-sm text-muted-foreground">
                  Amending run — adjust any line item; gross and net recalculate automatically. Saving re-finalizes the run and logs before/after values.
                </p>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm min-w-[1150px]">
                    <thead>
                      <tr className="border-b text-left text-muted-foreground">
                        <th className="py-2 pr-3 font-medium">Employee</th>
                        {["Basic", "Tips", "Gross", "Advance", "Uniform", "Unpaid leave", "UIF", "PAYE", "Net"].map((h) => (
                          <th key={h} className="py-2 pr-3 font-medium text-right">{h}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {selected.lines.map((l) => {
                        const v = amendVals[l.employee_id] || {};
                        const gross = (Number(v.basic_pay) || 0) + (Number(v.tips) || 0);
                        const net = gross - ((Number(v.advance) || 0) + (Number(v.uniform) || 0) + (Number(v.unpaid_leave) || 0) + (Number(v.uif) || 0) + (Number(v.paye) || 0));
                        return (
                          <tr key={l.employee_id} className="border-b last:border-0" data-testid={`amend-row-${l.employee_id}`}>
                            <td className="py-2 pr-3 font-medium">{l.name}</td>
                            {["basic_pay", "tips"].map((f) => (
                              <td key={f} className="py-2 pr-3 num">
                                <Input type="number" step="any" className="w-24 h-8 text-right ml-auto" data-testid={`amend-${f}-${l.employee_id}`}
                                  value={v[f] ?? 0}
                                  onChange={(e) => setAmendVals((m) => ({ ...m, [l.employee_id]: { ...m[l.employee_id], [f]: e.target.value } }))} />
                              </td>
                            ))}
                            <td className="py-2 pr-3 num font-medium">{zar(gross)}</td>
                            {["advance", "uniform", "unpaid_leave", "uif", "paye"].map((f) => (
                              <td key={f} className="py-2 pr-3 num">
                                <Input type="number" step="any" className="w-24 h-8 text-right ml-auto" data-testid={`amend-${f}-${l.employee_id}`}
                                  value={v[f] ?? 0}
                                  onChange={(e) => setAmendVals((m) => ({ ...m, [l.employee_id]: { ...m[l.employee_id], [f]: e.target.value } }))} />
                              </td>
                            ))}
                            <td className="py-2 pr-3 num font-semibold">{zar(net)}</td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
                <div className="flex gap-2 justify-end">
                  <Button variant="outline" onClick={() => setAmending(false)} data-testid="amend-cancel-btn">Cancel</Button>
                  <Button onClick={saveAmend} disabled={busy} data-testid="amend-save-btn" className="bg-primary hover:bg-[#8B5CF6] text-white">
                    {busy ? "Saving…" : "Save & re-finalize"}
                  </Button>
                </div>
              </div>
            ) : linesTable(selected.lines, false)}
          </CardContent>
        </Card>
      )}

      <Card className="border-border shadow-none" data-testid="runs-list">
        <CardHeader><CardTitle className="font-heading text-lg">Previous runs</CardTitle></CardHeader>
        <CardContent>
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b text-left text-muted-foreground">
                <th className="py-2 pr-4 font-medium">Period</th>
                <th className="py-2 pr-4 font-medium">Created</th>
                <th className="py-2 pr-4 font-medium text-right">Gross</th>
                <th className="py-2 pr-4 font-medium text-right">Net</th>
                <th className="py-2 font-medium"></th>
              </tr>
            </thead>
            <tbody>
              {runs.map((r) => (
                <tr key={r.id} className="border-b last:border-0" data-testid={`run-row-${r.id}`}>
                  <td className="py-2 pr-4 font-medium">{r.period_start} → {r.period_end}</td>
                  <td className="py-2 pr-4">{fmtDateTime(r.created_at)}</td>
                  <td className="py-2 pr-4 num">{zar(r.totals?.gross)}</td>
                  <td className="py-2 pr-4 num">{zar(r.totals?.net)}</td>
                  <td className="py-2 text-right">
                    <Button variant="outline" size="sm" data-testid={`view-run-${r.id}`} onClick={() => openRun(r.id)}>View</Button>
                  </td>
                </tr>
              ))}
              {runs.length === 0 && <tr><td colSpan={5} className="py-8 text-center text-muted-foreground">No payroll runs yet.</td></tr>}
            </tbody>
          </table>
        </CardContent>
      </Card>
    </div>
  );
}
